/* eslint-disable @typescript-eslint/no-explicit-any */
import { Link, useParams } from 'react-router-dom'
import { useMemo, useState } from 'react'
import Loading from '@/components/Loading'
import { DataTable } from '@/components/CustomTable'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useDepotQuery, useDepotMovesQuery } from '@/hooks/useDepots'
import type { ColumnDef } from '@tanstack/react-table'
import type { Container } from '@/types/backend'

type MoveRow = {
  createdAt?: string
  occurredAt?: string
  name?: 'Stocker' | 'Destocker' // compat V3
  type?:
    | 'PURCHASE'
    | 'ADJUST_UP'
    | 'ADJUST_DOWN'
    | 'TRANSFER_IN'
    | 'TRANSFER_OUT'
    | 'SALE'
    | string
  quantity?: number // V3
  qty?: number // V4
  item?: { id: string; name: string; sku?: string | null }
  note?: string | null
}

export default function DepotDetailPage() {
  const { id } = useParams<{ id: string }>()
  const [tab, setTab] = useState<'containers' | 'moves'>('containers')

  const { data: depot, status, isPending } = useDepotQuery(id!, !!id)
  const { data: moves } = useDepotMovesQuery(id, !!id)

  // -------- colonnes CONTAINERS
  const containerCols = useMemo<ColumnDef<Container>[]>(
    () => [
      {
        accessorKey: 'name',
        header: 'Conteneur',
        cell: ({ row }) => (
          <Link
            className='font-medium underline-offset-2 hover:underline'
            to={`/containers/${row.original.id}`}
          >
            {row.original.name}
          </Link>
        ),
      },
      {
        accessorKey: 'isDefault',
        header: 'Défaut',
        cell: ({ row }) => (row.original.isDefault ? '★ Oui' : '—'),
      },
      {
        accessorKey: 'description',
        header: 'Description',
        cell: ({ row }) => row.original.description ?? '—',
      },
    ],
    [],
  )

  // -------- colonnes MOVES (compat V3/V4)
  const moveCols = useMemo<ColumnDef<MoveRow>[]>(
    () => [
      {
        accessorKey: 'occurredAt',
        header: 'Date',
        cell: ({ row }) => {
          const d = row.original.occurredAt ?? row.original.createdAt
          return d ? new Date(d).toLocaleString() : '—'
        },
      },
      {
        accessorKey: 'type',
        header: 'Type',
        cell: ({ row }) => {
          const t = row.original.type
          const n = row.original.name
          if (n === 'Stocker') return 'Ajustement (+)'
          if (n === 'Destocker') return 'Ajustement (−)'
          return humanType(t ?? '')
        },
      },
      {
        accessorKey: 'item',
        header: 'Article',
        cell: ({ row }) =>
          row.original.item ? (
            <Link
              className='underline-offset-2 hover:underline'
              to={`/items/${row.original.item.id}`}
            >
              {row.original.item.name}
            </Link>
          ) : (
            '—'
          ),
      },
      {
        accessorKey: 'qty',
        header: 'Qté',
        cell: ({ row }) => {
          const q = row.original.quantity ?? row.original.qty ?? 0
          const s =
            row.original.name === 'Stocker'
              ? +1
              : row.original.name === 'Destocker'
                ? -1
                : signFor(row.original.type ?? '')
          const val = s * Math.abs(q)
          return (
            <span
              className={`tabular-nums ${
                val > 0 ? 'text-green-600' : val < 0 ? 'text-red-600' : ''
              }`}
            >
              {val}
            </span>
          )
        },
      },
      {
        accessorKey: 'note',
        header: 'Note',
        cell: ({ row }) => row.original.note ?? '—',
      },
    ],
    [],
  )

  // -------- rows
  const containers: Container[] = (depot?.containers ?? []) as any

  const moveRows: MoveRow[] = useMemo(() => {
    // V3: moves.products
    const v3 = (moves as any)?.products ?? null
    if (Array.isArray(v3)) return v3
    // V4: si tu exposes /depots/:id/moves avec même shape que container.moves
    return ((moves as any)?.rows ?? []) as MoveRow[]
  }, [moves])

  // -------- total (compat V3 via compte; sinon somme des entrées-sorties)
  const totalBal = useMemo(() => {
    const compte = (moves as any)?.compte as
      | Array<{
          _id: string
          totalQuantity: number
        }>
      | undefined
    const viaV3 = compte?.find((c) => c._id === id)?.totalQuantity
    if (typeof viaV3 === 'number') return viaV3

    // fallback : calcule net (entrées - sorties)
    if (!moveRows.length) return 0
    let ins = 0,
      outs = 0
    for (const m of moveRows) {
      const q = Math.abs(m.quantity ?? m.qty ?? 0)
      const s =
        m.name === 'Stocker'
          ? +1
          : m.name === 'Destocker'
            ? -1
            : signFor(m.type ?? '')
      if (s > 0) ins += q
      else if (s < 0) outs += q
    }
    return ins - outs
  }, [moves, moveRows, id])

  // -------- helpers
  function signFor(t: string) {
    switch (t) {
      case 'PURCHASE':
      case 'ADJUST_UP':
      case 'TRANSFER_IN':
        return +1
      case 'ADJUST_DOWN':
      case 'TRANSFER_OUT':
      case 'SALE':
        return -1
      default:
        return 0
    }
  }
  function humanType(t: string) {
    const map: Record<string, string> = {
      PURCHASE: 'Entrée (Achat)',
      ADJUST_UP: 'Ajustement (+)',
      ADJUST_DOWN: 'Ajustement (−)',
      TRANSFER_IN: 'Transfert (Entrée)',
      TRANSFER_OUT: 'Transfert (Sortie)',
      SALE: 'Sortie (Vente)',
    }
    return map[t] ?? t
  }

  if (status === 'pending') return <Loading />
  if (!depot) return <div className='p-4'>Dépôt introuvable.</div>

  return (
    <div className='space-y-6'>
      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <Link to='/depots'>Dépôts</Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage>{depot.name}</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>

      <div className='flex items-center flex-col gap-y-4 sm:flex-row justify-between'>
        <h2 className='text-2xl font-semibold'>
          {depot.name}{' '}
          <span className='text-sm text-muted-foreground'>
            #{depot.id.slice(0, 8)}
          </span>
        </h2>
      </div>

      <div className='grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4'>
        <Info label='Adresse' value={depot.address ?? '—'} />
        <Info label='Code' value={depot.code ?? '—'} />
        <Info label='Par défaut' value={depot.isDefault ? 'Oui' : 'Non'} />
        <div className='rounded-lg border p-3'>
          <div className='text-xs text-muted-foreground'>Total</div>
          <div className='mt-1 font-semibold tabular-nums'>{totalBal}</div>
        </div>
      </div>

      <Tabs value={tab} onValueChange={(v) => setTab(v as any)}>
        <TabsList>
          <TabsTrigger value='containers'>Conteneurs</TabsTrigger>
          <TabsTrigger value='moves'>Flux</TabsTrigger>
        </TabsList>

        <TabsContent value='containers' className='mt-4'>
          <div className='text-sm text-muted-foreground mb-2'>
            Lignes: <b>{containers.length}</b>{' '}
            {isPending ? <span className='ml-2'>Actualisation…</span> : null}
          </div>
          <DataTable<Container, unknown>
            columns={containerCols}
            data={containers}
          />
        </TabsContent>

        <TabsContent value='moves' className='mt-4'>
          <DataTable<MoveRow, unknown> columns={moveCols} data={moveRows} />
          {!moveRows.length ? (
            <div className='text-sm text-muted-foreground mt-2'>
              Aucun flux disponible pour ce dépôt.
            </div>
          ) : null}
        </TabsContent>
      </Tabs>
    </div>
  )
}

function Info({ label, value }: { label: string; value: string }) {
  return (
    <div className='rounded-lg border p-3'>
      <div className='text-xs text-muted-foreground'>{label}</div>
      <div className='mt-1 font-medium break-all'>{value}</div>
    </div>
  )
}
